import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { CREATE_DISABILITY_HACK, UPDATE_HACK_FIELDS, CREATE_MEDIA_ITEM, SET_FEATURED_IMAGE } from '../lib/mutations';
import { useNavigate } from 'react-router-dom';
import { wpClient } from '../lib/wordpress';

// Simple test query to check if WordPress is accessible
const TEST_QUERY = gql`
  query TestConnection {
    generalSettings {
      title
    }
  }
`;

interface FormData {
  title: string;
  platform: string;
  videoUrl: string;
  summary: string;
  hackDescription: string;
  hackCategory: string;
  otKeywords: string;
  source: string;
  userTested: string;
  otUsefulnessRating: number;
  imageFile: File | null;
  // These fields will likely be filled out by admin after review
  reviewedBy: string;
  difficultyLevel: string;
}

const HackSubmissionForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    title: '',
    platform: '',
    videoUrl: '',
    summary: '',
    hackDescription: '',
    hackCategory: '',
    otKeywords: '',
    source: '',
    userTested: 'No',
    otUsefulnessRating: 0,
    imageFile: null,
    reviewedBy: '',
    difficultyLevel: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');

  const [createDisabilityHack] = useMutation(CREATE_DISABILITY_HACK);
  const [updateHackFields] = useMutation(UPDATE_HACK_FIELDS);
  const [createMediaItem] = useMutation(CREATE_MEDIA_ITEM);
  const [setFeaturedImage] = useMutation(SET_FEATURED_IMAGE);

  // Function to test WordPress GraphQL connection
  const testConnection = async () => {
    try {
      setConnectionStatus('unknown');
      
      // Log the current endpoint for debugging
      const endpoint = import.meta.env.VITE_WORDPRESS_API_URL || 'http://localhost:10212/graphql';
      console.log('Testing connection to WordPress at:', endpoint);
      
      const result = await wpClient.query({
        query: TEST_QUERY,
        fetchPolicy: 'no-cache'
      });
      
      if (result.data?.generalSettings?.title) {
        console.log('Connection successful! WordPress site title:', result.data.generalSettings.title);
        setConnectionStatus('connected');
        setSuccessMessage(`Connected to WordPress: ${result.data.generalSettings.title}`);
      } else {
        console.error('Connection test failed - no title returned');
        setConnectionStatus('error');
        setError('Could not get WordPress site title. Check your connection.');
      }
    } catch (err) {
      console.error('WordPress connection test error:', err);
      setConnectionStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to connect to WordPress');
    }
  };
  
  // Test connection on component mount
  useEffect(() => {
    testConnection();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    // Check connection before submitting
    if (connectionStatus === 'error') {
      setError('Cannot submit while disconnected from WordPress. Please check your connection.');
      setIsSubmitting(false);
      return;
    }

    // Ensure we have at least a default title if the field is empty
    const submissionData = {
      ...formData,
      title: formData.title || 'Untitled Hack',
      summary: formData.summary || '',
      hackDescription: formData.hackDescription || '',
      hackCategory: formData.hackCategory || 'other',
    };

    try {
      console.log('Attempting to create disability hack post...');
      
      // Step 1: Create the disability hack post with DRAFT status
      const createResult = await createDisabilityHack({
        variables: {
          input: {
            title: submissionData.title,
            status: "DRAFT", // Ensure it's saved as a draft for review
            clientMutationId: `hack-${Date.now()}`
          }
        },
      });

      console.log('Create post result:', createResult);

      if (!createResult.data?.createDisabilityHack?.disabilityHack?.id) {
        throw new Error('Failed to create hack submission - no ID returned');
      }

      const postId = createResult.data.createDisabilityHack.disabilityHack.id;
      const postDatabaseId = createResult.data.createDisabilityHack.disabilityHack.databaseId;
      
      console.log('Post created successfully with ID:', postId);

      // Step 2: Update the ACF fields based on the actual field structure
      console.log('Updating ACF fields...');
      
      const updateResult = await updateHackFields({
        variables: {
          id: postId,
          title: submissionData.title,
          platform: submissionData.platform || "",
          videoUrl: submissionData.videoUrl || "",
          summary: submissionData.summary,
          hackDescription: submissionData.hackDescription,
          hackCategory: submissionData.hackCategory,
          otKeywords: submissionData.otKeywords || "",
          source: submissionData.source || "",
          userTested: submissionData.userTested || "No",
          otUsefulnessRating: submissionData.otUsefulnessRating || 0,
          reviewedBy: submissionData.reviewedBy || "",
          difficultyLevel: submissionData.difficultyLevel || "",
        },
      });

      console.log('ACF fields update result:', updateResult);

      // Step 3: If there's an image file, upload it using WordPress REST API
      if (formData.imageFile) {
        try {
          // Get the WordPress site URL from localStorage or environment variable
          const wpBaseUrl = import.meta.env.VITE_WORDPRESS_URL || 'http://localhost:10212';
          
          // Create FormData for file upload
          const uploadData = new FormData();
          uploadData.append('file', formData.imageFile);
          uploadData.append('title', `Image for ${formData.title}`);
          uploadData.append('alt_text', `Image for ${formData.title} disability hack`);
          
          // Upload the file to the WordPress REST API endpoint
          const uploadResponse = await fetch(`${wpBaseUrl}/wp-json/wp/v2/media`, {
            method: 'POST',
            body: uploadData,
            headers: {
              // WordPress REST API requires authorization for media uploads
              // This assumes your WP site is configured to accept anonymous uploads
              // If not, you'll need to implement an authentication mechanism
            },
          });

          if (uploadResponse.ok) {
            const mediaData = await uploadResponse.json();
            
            // Set the media item as the featured image
            if (mediaData.id) {
              await setFeaturedImage({
                variables: {
                  postId: postId,
                  mediaId: mediaData.id.toString()
                }
              });
            }
          } else {
            console.warn('Image upload failed, but hack was created');
          }
        } catch (uploadError) {
          console.warn('Image upload failed, but hack was created:', uploadError);
        }
      }

      setSuccessMessage("Your hack has been submitted successfully and is awaiting review!");
      
      // Reset form after successful submission
      setFormData({
        title: '',
        platform: '',
        videoUrl: '',
        summary: '',
        hackDescription: '',
        hackCategory: '',
        otKeywords: '',
        source: '',
        userTested: 'No',
        otUsefulnessRating: 0,
        imageFile: null,
        reviewedBy: '',
        difficultyLevel: '',
      });
      
      // Navigate to the confirmation page after a short delay
      setTimeout(() => {
        navigate('/submit-confirmation', { 
          state: { 
            title: submissionData.title
          } 
        });
      }, 2000);
    } catch (err) {
      console.error('Submission error:', err);
      let errorMessage = 'An error occurred during submission';
      
      if (err instanceof Error) {
        // More user-friendly error messages
        if (err.message.includes('Failed to create hack submission')) {
          errorMessage = 'Unable to submit hack at this time. Please try again later.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseInt(value) || 0,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData(prev => ({
        ...prev,
        imageFile: e.target.files![0],
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6 bg-white shadow-md rounded-lg">
      <div className="space-y-6">
        {/* WordPress Connection Status */}
        <div className="border p-4 rounded-md">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">WordPress Connection Status: 
              <span className={
                connectionStatus === 'connected' ? 'text-green-600 ml-2' :
                connectionStatus === 'error' ? 'text-red-600 ml-2' :
                'text-gray-600 ml-2'
              }>
                {connectionStatus === 'connected' ? 'Connected' :
                 connectionStatus === 'error' ? 'Error' :
                 'Checking...'}
              </span>
            </h3>
            <button 
              type="button"
              onClick={testConnection}
              className="px-3 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              Test Connection
            </button>
          </div>
          <p className="text-xs text-gray-600">
            Using endpoint: {import.meta.env.VITE_WORDPRESS_API_URL || 'http://localhost:10212/graphql'}
          </p>
        </div>
        
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Give your hack a clear, descriptive title"
          />
        </div>

        <div>
          <label htmlFor="platform" className="block text-sm font-medium text-gray-700">
            Platform
          </label>
          <input
            type="text"
            id="platform"
            name="platform"
            value={formData.platform}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="What platform is this hack for? (e.g., iOS, Android, Web)"
          />
        </div>

        <div>
          <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-700">
            Video URL (optional)
          </label>
          <input
            type="url"
            id="videoUrl"
            name="videoUrl"
            value={formData.videoUrl}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="YouTube or Vimeo link showing your hack in action"
          />
          <p className="mt-1 text-xs text-gray-500">
            A video demonstration helps others understand your hack better
          </p>
        </div>

        <div>
          <label htmlFor="summary" className="block text-sm font-medium text-gray-700">
            Summary
          </label>
          <textarea
            id="summary"
            name="summary"
            value={formData.summary}
            onChange={handleChange}
            rows={2}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Brief summary of your hack"
          />
        </div>

        <div>
          <label htmlFor="hackDescription" className="block text-sm font-medium text-gray-700">
            Hack Description
          </label>
          <textarea
            id="hackDescription"
            name="hackDescription"
            value={formData.hackDescription}
            onChange={handleChange}
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Describe how your hack works, what problem it solves, and who it helps"
          />
        </div>

        <div>
          <label htmlFor="hackCategory" className="block text-sm font-medium text-gray-700">
            Hack Category
          </label>
          <select
            id="hackCategory"
            name="hackCategory"
            value={formData.hackCategory}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select a category</option>
            <option value="mobility">Mobility</option>
            <option value="vision">Vision</option>
            <option value="hearing">Hearing</option>
            <option value="cognitive">Cognitive</option>
            <option value="dexterity">Dexterity</option>
            <option value="communication">Communication</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="otKeywords" className="block text-sm font-medium text-gray-700">
            Keywords (optional)
          </label>
          <input
            type="text"
            id="otKeywords"
            name="otKeywords"
            value={formData.otKeywords}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="e.g., wheelchair, kitchen, low-cost, DIY"
          />
          <p className="mt-1 text-xs text-gray-500">
            Comma-separated keywords to help others find your hack
          </p>
        </div>

        <div>
          <label htmlFor="source" className="block text-sm font-medium text-gray-700">
            Source (optional)
          </label>
          <input
            type="text"
            id="source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Where did this hack originate from?"
          />
        </div>

        <div>
          <label htmlFor="userTested" className="block text-sm font-medium text-gray-700">
            User Tested
          </label>
          <select
            id="userTested"
            name="userTested"
            value={formData.userTested}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        <div>
          <label htmlFor="otUsefulnessRating" className="block text-sm font-medium text-gray-700">
            Usefulness Rating (1-5)
          </label>
          <input
            type="number"
            id="otUsefulnessRating"
            name="otUsefulnessRating"
            min="0"
            max="5"
            value={formData.otUsefulnessRating}
            onChange={handleNumberChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="imageFile" className="block text-sm font-medium text-gray-700">
            Image (optional)
          </label>
          <input
            type="file"
            id="imageFile"
            name="imageFile"
            accept="image/*"
            onChange={handleFileChange}
            className="mt-1 block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
          <p className="mt-1 text-xs text-gray-500">
            Upload an image showing your hack (max 5MB)
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {successMessage && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="text-sm text-green-700">{successMessage}</div>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isSubmitting || connectionStatus === 'error'}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : connectionStatus === 'error' ? 'WordPress Disconnected' : 'Submit Hack for Review'}
          </button>
        </div>
        
        <p className="text-xs text-gray-500 text-center">
          * All submissions are reviewed by our team before being published
        </p>
      </div>
    </form>
  );
};

export default HackSubmissionForm; 